/* global snaptr */

import { useState, useEffect, useRef } from "react";
import { v4 as uuidv4 } from 'uuid';
import { Link } from "react-router-dom";
import { getFirestore } from "firebase/firestore";
import { getAnalytics, logEvent } from "firebase/analytics";
import { app } from "../../firebase-config";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { GooglePlayButton, AppStoreButton } from "react-mobile-app-button";
import styles from "./ColdLead.module.css";

import {
  ArrowRight,
  CheckCircle2,
  Eye,
  Brain,
  Zap,
  Shield,
  ChevronDown,
  Sparkles,
  Clock,
} from "lucide-react";

// Interest options for the dropdown
const INTEREST_OPTIONS = [
  { value: "", label: "What brings you here?", description: "" },
  { value: "quit_myself", label: "🚭 I want to quit vaping", description: "Personal use" },
  { value: "reduce", label: "📉 I want to cut back", description: "Reduce usage gradually" },
  { value: "help_someone", label: "💙 Helping someone else", description: "Friend or family member" },
  { value: "healthcare", label: "🏥 Healthcare provider", description: "Clinical interest" },
  { value: "research", label: "🔬 Researcher", description: "Academic or clinical research" },
  { value: "employer", label: "🏢 Employer / HR", description: "Workplace wellness" },
  { value: "investor", label: "💰 Investor / Funding", description: "Investment opportunity inquiry" },
  { value: "curious", label: "👀 Just curious", description: "Learning more" },
];

const ColdLead = () => {
  const [email, setEmail] = useState("");
  const [interest, setInterest] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState("");
  const formRef = useRef(null);
  const dropdownRef = useRef(null);
  
  const analytics = getAnalytics(app);

  // App store URLs
  const iosUrl = "https://apps.apple.com/us/app/qwytt-nicotine/id6657960125";
  const androidUrl = "https://play.google.com/store/apps/details?id=com.qwytt.nicotine.prod";

  const selectedOption = INTEREST_OPTIONS.find(opt => opt.value === interest);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Track page view with source parameters
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const source = params.get("utm_source") || "direct";
    const campaign = params.get("utm_campaign") || "none";
    const medium = params.get("utm_medium") || "none";

    logEvent(analytics, "page_view", {
      page_title: "Cold Lead Landing",
      page_location: window.location.href,
      page_path: "/learn",
      traffic_source: source,
      campaign: campaign,
      medium: medium,
    });

    // Snapchat pixel
    if (window.snaptr) {
      snaptr("track", "PAGE_VIEW", {
        page_url: window.location.href,
      });
    }
  }, [analytics]);

  function getSnapClickId() {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get("sc_click_id") || undefined;
    } catch {
      return undefined;
    }
  }

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth" });
    logEvent(analytics, "cta_click", {
      location: "hero",
      action: "scroll_to_form",
      page: "cold_lead",
    });
  };

  const handleInterestSelect = (value) => {
    setInterest(value);
    setIsDropdownOpen(false);
    
    logEvent(analytics, "interest_selected", {
      interest_type: value,
      page: "cold_lead"
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!email || !email.includes("@")) {
      setError("Please enter a valid email address");
      return;
    }

    if (!interest) {
      setError("Please select what brings you here");
      return;
    }

    setIsSubmitting(true);

    try {
      const params = new URLSearchParams(window.location.search);
      const db = getFirestore(app);

      await addDoc(collection(db, "coldLeads"), {
        email: email.toLowerCase().trim(),
        interest: interest,
        source: params.get("utm_source") || "direct",
        campaign: params.get("utm_campaign") || null,
        medium: params.get("utm_medium") || null,
        content: params.get("utm_content") || null,
        landingPage: "/learn",
        createdAt: serverTimestamp(),
        unsubscribeToken: uuidv4(),
        unsubscribed: false,
        snapClickId: getSnapClickId() || null,
      });

      logEvent(analytics, "generate_lead", {
        currency: "USD",
        value: 1,
        page: "cold_lead",
        interest_type: interest,
        source: params.get("utm_source") || "direct",
      });

      logEvent(analytics, "sign_up", {
        method: "email",
        page: "cold_lead",
        interest_type: interest,
      });

      if (window.snaptr) {
        snaptr("track", "SIGN_UP", {
          sign_up_method: "email",
          uuid_c1: getSnapClickId(),
        });
      }

      setIsSubmitted(true);
      setEmail("");
      setInterest("");
    } catch (err) {
      console.error("Error saving lead:", err);
      setError("Something went wrong. Please try again.");
      
      logEvent(analytics, "lead_error", {
        error: err.message,
        page: "cold_lead",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLearnMoreClick = () => {
    logEvent(analytics, "cta_click", {
      location: "success_state",
      action: "learn_more",
      page: "cold_lead",
    });
  };

  const handleDownloadClick = (platform) => {
    logEvent(analytics, "cta_click", {
      location: "success_state",
      action: "download_app",
      platform: platform,
      page: "cold_lead",
    });

    if (window.snaptr) {
      snaptr("track", "APP_OPEN", {
        destination: `app_store_${platform}`,
        uuid_c1: getSnapClickId(),
      });
    }
  };

  return (
    <div className={styles.page}>
      {/* ========== HERO ========== */}
      <section className={styles.hero}>
        <div className={styles.heroContent}>
          <div className={styles.badge}>
            <Sparkles size={14} />
            <span>Science-backed quit method</span>
          </div>

          <h1 className={styles.headline}>
            You Don't Need<br />
            <span className={styles.highlight}>Willpower</span> to Quit Vaping
          </h1>

          <p className={styles.subheadline}>
            95% of cold-turkey attempts fail. Qwytt uses your own data to build 
            a personalized reduction plan that actually works.
          </p>

          <button onClick={scrollToForm} className={styles.heroCta}>
            <span>See How It Works</span>
            <ChevronDown size={20} className={styles.bounceIcon} />
          </button>

          <div className={styles.trustIndicators}>
            <div className={styles.trustItem}>
              <Shield size={16} />
              <span>Patent-pending tech</span>
            </div>
            <div className={styles.trustItem}>
              <Clock size={16} />
              <span>Free app available now</span>
            </div>
          </div>
        </div>

        <div className={styles.heroGraphic}>
          <img
            className={styles.cloudImage}
            src="/images/cloud.jpg"
            alt="Vape-cloud background"
          />
          <div className={styles.heroDevices}>
            <img
              className={styles.dashImage}
              src="/images/dash.png"
              alt="Qwytt dashboard showing usage tracking"
            />
            <img
              className={styles.sensorOverlay}
              src="/images/cuitt_angled.png"
              alt="Qwytt Sensor"
            />
          </div>
        </div>
      </section>

      {/* ========== THE PROBLEM ========== */}
      <section ref={formRef} className={styles.problemSection}>
        <div className={styles.problemContent}>
          <h2 className={styles.sectionTitle}>
            Why You Haven't Quit Yet
          </h2>
          
          <div className={styles.problemGrid}>
            <div className={styles.problemCard}>
        <div className={styles.problemNumber}>01</div>
        <h3>You can't see the pattern</h3>
        <p>
          Vaping feels random, but there's 
          a pattern. You just don't have the data to see it yet.
        </p>
      </div>
                  <div className={styles.problemCard}>
                    <div className={styles.problemNumber}>02</div>
                    <h3>Quitting immediately fails of the time</h3>
                    <p>
                      Your brain adapted to nicotine over months or years. 
                      It needs a gradual path out, not a cliff.
                    </p>
                  </div>

            <div className={styles.problemCard}>
              <div className={styles.problemNumber}>03</div>
              <h3>Willpower runs out</h3>
              <p>
                Stress, boredom, social pressure, eventually you 
                break. You need a system, not just motivation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== THE SOLUTION ========== */}
      <section className={styles.solutionSection}>
        <div className={styles.solutionContent}>
          <span className={styles.sectionLabel}>The Qwytt Method</span>
          <h2 className={styles.sectionTitle}>
            Data-Driven Reduction.<br />No Cold Turkey Required.
          </h2>

          <div className={styles.solutionSteps}>
            <div className={styles.solutionStep}>
              <div className={styles.stepIcon}>
                <Eye size={24} />
              </div>
              <div className={styles.stepContent}>
                <h3>Track Your Reality</h3>
                <p>
                  Our sensor automatically logs every puff. No manual 
                  tracking. No guessing. Just objective data about 
                  your actual habits.
                </p>
              </div>
            </div>

            <div className={styles.solutionStep}>
              <div className={styles.stepIcon}>
                <Brain size={24} />
              </div>
              <div className={styles.stepContent}>
                <h3>Get Personalized Goals</h3>
                <p>
                  After 7 days, our AI creates reduction targets based 
                  on YOUR patterns, not generic advice that ignores 
                  your life.
                </p>
              </div>
            </div>

            <div className={styles.solutionStep}>
              <div className={styles.stepIcon}>
                <Zap size={24} />
              </div>
              <div className={styles.stepContent}>
                <h3>Reduce Gradually</h3>
                <p>
                  Cut back at your own pace. Real-time feedback keeps 
                  you on track. Goals adapt when life gets hard.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.solutionVisual}>
          <img
            src="/images/cuitt_angled.png"
            alt="Qwytt sensor"
            className={styles.sensorImage}
          />
        </div>
      </section>

      {/* ========== SOCIAL PROOF ========== */}
      <section className={styles.proofSection}>
        <div className={styles.proofContent}>
          <div className={styles.statGrid}>
            <div className={styles.statCard}>
              <div className={styles.statValue}>40%</div>
              <div className={styles.statLabel}>
                average reduction with real-time tracking*
              </div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statValue}>7 days</div>
              <div className={styles.statLabel}>
                to personalized goals
              </div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statValue}>2+ mo</div>
              <div className={styles.statLabel}>
                battery life per charge
              </div>
            </div>
          </div>
          <p className={styles.citation}>
            *Based on peer-reviewed research on passive smoking detection (Dar, 2018)
          </p>
        </div>
      </section>

      {/* ========== EMAIL CAPTURE ========== */}
      <section className={styles.captureSection}>
        <div className={styles.captureContent}>
          {!isSubmitted ? (
            <>
              <h2 className={styles.captureTitle}>
                Ready to See Your Real Numbers?
              </h2>
              <p className={styles.captureSubtitle}>
                Get early access to Qwytt and learn how data-driven 
                nicotine reduction can work for you.
              </p>

              <form onSubmit={handleSubmit} className={styles.captureForm}>
                <div className={styles.inputWrapper}>
                  <input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={styles.emailInput}
                    disabled={isSubmitting}
                  />
                </div>

                {/* Interest Dropdown */}
                <div ref={dropdownRef} className={styles.dropdownContainer}>
                  <button
                    type="button"
                    className={`${styles.dropdownTrigger} ${interest ? styles.hasValue : ""} ${isDropdownOpen ? styles.open : ""}`}
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    aria-haspopup="listbox"
                    aria-expanded={isDropdownOpen}
                    disabled={isSubmitting}
                  >
                    <span className={styles.dropdownLabel}>
                      {selectedOption?.label || "What brings you here?"}
                    </span>
                    <ChevronDown 
                      size={20} 
                      className={`${styles.dropdownArrow} ${isDropdownOpen ? styles.rotated : ""}`}
                    />
                  </button>

                  {isDropdownOpen && (
                    <ul className={styles.dropdownMenu} role="listbox">
                      {INTEREST_OPTIONS.filter(opt => opt.value !== "").map((option) => (
                        <li
                          key={option.value}
                          role="option"
                          aria-selected={interest === option.value}
                          className={`${styles.dropdownOption} ${interest === option.value ? styles.selected : ""}`}
                          onClick={() => handleInterestSelect(option.value)}
                        >
                          <span className={styles.optionLabel}>{option.label}</span>
                          {option.description && (
                            <span className={styles.optionDescription}>{option.description}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <button
                  type="submit"
                  className={styles.submitButton}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <span className={styles.spinner}></span>
                  ) : (
                    <>
                      <span>Get Early Access</span>
                      <ArrowRight size={18} />
                    </>
                  )}
                </button>

                {error && <p className={styles.errorText}>{error}</p>}
              </form>

              <div className={styles.capturePerks}>
                <div className={styles.perk}>
                  <CheckCircle2 size={16} />
                  <span>Free app access</span>
                </div>
                <div className={styles.perk}>
                  <CheckCircle2 size={16} />
                  <span>Sensor pre-order priority</span>
                </div>
              </div>
            </>
          ) : (
            <div className={styles.successState}>
              <div className={styles.successIcon}>
                <CheckCircle2 size={48} />
              </div>
              <h2 className={styles.successTitle}>You're In!</h2>
              <p className={styles.successText}>
                Check your inbox for next steps. In the meantime, 
                you can download the free app and start tracking today.
              </p>
              <div className={styles.successActions}>
                {/* Updated app store buttons */}
                <div className={styles.appStoreLinks}>
                  <div onClick={() => handleDownloadClick('ios')}>
                    <AppStoreButton 
                      url={iosUrl} 
                      theme="dark"
                    />
                  </div>
                  <div onClick={() => handleDownloadClick('android')}>
                    <GooglePlayButton 
                      url={androidUrl} 
                      theme="dark"
                    />
                  </div>
                </div>
                <Link
                  to="/reserve"
                  className={styles.successSecondary}
                  onClick={handleLearnMoreClick}
                >
                  Learn About the Sensor →
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ========== FOOTER NOTE ========== */}
      <footer className={styles.footer}>
        <p>
          Questions? Email us at{" "}
          <a href="mailto:contact@qwytt.com">contact@qwytt.ai</a>
        </p>
        <div className={styles.footerLinks}>
          <Link to="/">Full Site</Link>
          <span>·</span>
          <Link to="/research">Research</Link>
          <span>·</span>
          <a
            href="https://patentscope.wipo.int/search/en/detail.jsf?docId=WO2024173401"
            target="_blank"
            rel="noopener noreferrer"
          >
            Patent
          </a>
        </div>
      </footer>
    </div>
  );
};

export default ColdLead;